import { type TypeTableColumn } from '@thefarmersfront/kitchen';

import { TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT, DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR } from '~/constants';

import { StatusBadge, joinOrDash } from './deliveryAgencyTableCells';

import type { TypeDeliveryAgencyShippingLabel } from '~/types/delivery-agency.types';

const getDeliveryAgencyTrackingsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (order: TypeDeliveryAgencyShippingLabel) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeDeliveryAgencyShippingLabel>[] = [
    {
      id: 'rowNumber',
      header: 'No',
      cell: ({ row }) => row.index + 1 + currentPage * pageSize,
      size: 96,
      meta: { selection: true },
    },
    {
      accessorKey: 'shippingLabelStatus',
      header: '운송장 상태',
      size: 105,
      cell: ({ row }) => {
        const value = row.original.shippingLabelStatus;
        if (!value) return '-';

        return <StatusBadge value={value} colors={DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR} />;
      },
    },
    {
      accessorKey: 'statusUpdatedAt',
      header: '상태 마지막 업데이트 일시',
      size: 170,
      cell: ({ row }) => formatDate(row.original.statusUpdatedAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      accessorKey: 'shippingLabelNumber',
      header: '운송장 번호',
      size: 245,
      cell: ({ getValue, row }) => (
        <TextButtonCell value={getValue()} onClick={() => onClickViewDetail(row.original)}>
          {row.original.shippingLabelNumber}
        </TextButtonCell>
      ),
    },
    {
      id: 'isPrinted',
      accessorFn: (row) => (row.isPrinted ? '출력함' : '출력안함'),
      header: '운송장 출력',
      size: 113,
    },
    {
      accessorKey: 'outboundCode',
      header: '출고번호',
      size: 160,
    },
    {
      accessorKey: 'clientOrderCodes',
      header: '판매처 주문번호',
      size: 160,
      cell: ({ row }) => joinOrDash(row.original.clientOrderCodes),
    },
    {
      accessorKey: 'estimatedDeliveryDate',
      header: '배송 예정일',
      size: 100,
      cell: ({ row }) => formatDate(row.original.estimatedDeliveryDate, DATE_FORMAT.DATE) ?? '-',
    },
    {
      id: 'deliveryType',
      accessorFn: (row) => row.deliveryType?.description,
      header: '배송 유형',
      size: 80,
    },
    {
      id: 'courierCode',
      accessorFn: (row) => row.courierCode?.description,
      header: '배송사',
      size: 88,
    },
    {
      accessorKey: 'receiverName',
      header: '수령자명',
      size: 100,
      cell: ({ row }) => maskName(row.original.receiverName) ?? '-',
    },
    {
      accessorKey: 'ordererName',
      header: '주문자명',
      size: 100,
      cell: ({ row }) => maskName(row.original.ordererName) ?? '-',
    },
    {
      accessorKey: 'productName',
      header: '상품명',
      minSize: 252,
      meta: { flex: true },
    },
    {
      accessorKey: 'createdAt',
      header: '운송장 생성일시',
      size: 160,
      cell: ({ row }) => formatDate(row.original.createdAt, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyTrackingsColumnDefs;
